import React from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  useLazyGetContractPdfQuery,
  useLazyGetContractPdfByReservationQuery,
} from '../Service/extendedContractsApi';
import {
  downloadContractPdf,
  openContractPdfInNewTab,
  downloadContractPdfByReservation,
  openContractPdfInNewTabByReservation,
} from '../Utils/contractPdfUtils';

interface ContractPdfActionsProps {
  contractId?: number;
  reservationId?: number;
  contractName?: string;
  variant?: 'buttons' | 'icons';
  size?: 'small' | 'medium' | 'large';
}
const ContractPdfActions = ({
  contractId,
  reservationId,
  contractName,
  variant = 'buttons',
  size = 'medium',
}: ContractPdfActionsProps) => {
  const [triggerGetPdf] = useLazyGetContractPdfQuery();
  const [triggerGetPdfByReservation] = useLazyGetContractPdfByReservationQuery();

  const handleDownload = async () => {
    try {
      if (reservationId) {
        // Użyj endpointu do pobierania przez reservationId
        await downloadContractPdfByReservation(
          triggerGetPdfByReservation,
          reservationId,
          contractName
        );
      } else if (contractId) {
        // Użyj istniejącego endpointu przez contractId
        await downloadContractPdf(triggerGetPdf, contractId, contractName);
      } else {
        console.error('Nie podano contractId ani reservationId');
      }
    } catch (error: any) {
      console.error('Download failed:', error);

      // Czytelny komunikat dla użytkownika
      if (error?.status === 404) {
        alert(
          'Umowa nie jest dostępna. Kliknij przycisk "Wygeneruj / Regeneruj umowę", a następnie spróbuj ponownie.'
        );
      } else {
        alert('Błąd pobierania PDF. Sprawdź konsolę przeglądarki aby uzyskać więcej szczegółów.');
      }
    }
  };

  const handleOpenInNewTab = async () => {
    try {
      if (reservationId) {
        // Użyj endpointu do pobierania przez reservationId
        await openContractPdfInNewTabByReservation(triggerGetPdfByReservation, reservationId);
      } else if (contractId) {
        // Użyj istniejącego endpointu przez contractId
        await openContractPdfInNewTab(triggerGetPdf, contractId);
      } else {
        console.error('Nie podano contractId ani reservationId');
      }
    } catch (error: any) {
      console.error('Open PDF failed:', error);

      // Czytelny komunikat dla użytkownika
      if (error?.status === 404) {
        alert(
          'Umowa nie jest dostępna. Kliknij przycisk "Wygeneruj / Regeneruj umowę", a następnie spróbuj ponownie.'
        );
      } else {
        alert('Błąd otwierania PDF. Sprawdź konsolę przeglądarki aby uzyskać więcej szczegółów.');
      }
    }
  };

  if (variant === 'icons') {
    return (
      <>
        <Tooltip title="Pobierz PDF">
          <IconButton onClick={handleDownload} size={size} color="primary">
            <DownloadIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Otwórz PDF w nowej karcie">
          <IconButton onClick={handleOpenInNewTab} size={size} color="primary">
            <OpenInNewIcon />
          </IconButton>
        </Tooltip>
      </>
    );
  }

  return (
    <>
      <Button
        startIcon={<DownloadIcon />}
        onClick={handleDownload}
        size={size}
        variant="outlined"
        sx={{ mr: 1 }}
      >
        Pobierz PDF
      </Button>

      <Button
        startIcon={<OpenInNewIcon />}
        onClick={handleOpenInNewTab}
        size={size}
        variant="outlined"
      >
        Otwórz PDF
      </Button>
    </>
  );
};

export default ContractPdfActions;
